import { Component } from '@angular/core';
import { localizeRole, Role } from '../../model/auth/Role';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { User } from '../../model/auth/User';
import { MobileAwareComponent } from '../../common/mobile-aware/mobile-aware.component';
import { NotificationHandler } from '../../helper/notification.handler';
import {
  InviteUserDetailsData,
  InviteUserModalComponent,
  InviteUserResult
} from '../../profile/invite-user-modal/invite-user-modal.component';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import { Contest } from '../../model/contest/Contest';
import { TenantService } from '../../service/tenant.service';
import { ContestService } from '../../service/contest.service';
import { DEFAULT_PAGE, Page } from '../../model/Page';
import { UserDetails, UsersTableComponent } from '../../profile/users-table/users-table.component';

@Component({
  selector: 'app-coordinators-list',
  templateUrl: './coordinators-list.component.html',
  styleUrls: ['./coordinators-list.component.scss']
})
export class CoordinatorsListComponent extends MobileAwareComponent {

  readonly COORDINATOR_ROLE = Role.Coordinator;

  readonly roles = [Role.Host];

  inProgress = false;

  competitionId?: string;
  contest?: Contest;
  contestOpenForEnrol = false;

  coordinators!: User[] | null;
  hasLocallyAdded = false;

  page: Page<never> = DEFAULT_PAGE;

  constructor(private contestService: ContestService,
              private tenantService: TenantService,
              private dialog: MatDialog,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }
  }

  contestSelected(contest: Contest) {
    this.contest = contest;
    this.loadCoordinators(this.competitionId, this.contest, true);
  }

  loadCoordinators(competitionId?: string, contest?: Contest, reset = false) {
    if (!competitionId || !contest) return;

    this.inProgress = true;

    if (reset) {
      this.page = {
        max_items: 10,
        asc: true
      };
      this.coordinators = null;
    }

    this.contestService.getEnrolledUsers(competitionId, contest.id, Role.Coordinator, this.page).subscribe({
      next: page => {
        const data = page.content || [];

        if (!this.coordinators) {
          this.coordinators = data;
        } else {
          this.coordinators = this.coordinators.concat(data);
        }

        this.page.next_token = page.next_token;

        this.inProgress = false;
      },
      error: error => {
        this.inProgress = false;
        NotificationHandler.handleError($localize`:Notifications|Contest coordinators fetch has failed message@@notifications-contest-coordinators-fetch-failed-error:Contest coordinators fetch has failed`, error);
      }
    });
  }

  loadNextPage() {
    this.loadCoordinators(this.competitionId, this.contest);
  }

  assignCoordinator(contest?: Contest | null) {
    if (this.competitionId && contest) {
      const data: InviteUserDetailsData = {
        competitionId: this.competitionId,
        contest: contest,
        requestedRole: Role.Coordinator
      };
      this.assignCoordinatorInternal(data);
    }
  }

  private assignCoordinatorInternal(data: InviteUserDetailsData) {
    this.dialog.open(InviteUserModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((data: InviteUserResult) => {
      if (data && data.addMore && data.addMoreData) {
        if (data.user && this.coordinators) {
          this.coordinators.unshift(data.user);
          this.hasLocallyAdded = true;
        }
        this.assignCoordinatorInternal(data.addMoreData);
      } else if ((data && data.user) || this.hasLocallyAdded) {
        this.loadCoordinators(this.competitionId, this.contest, true);
        this.hasLocallyAdded = false;
      }
    });
  }

  removeCoordinator(details: UserDetails, usersTable: UsersTableComponent) {
    if (details.competitionId) {
      const data: DeleteConfirmationData = {
        title: $localize`:General|Remove action label@@general-remove-action-label:Remove`,
        itemType: localizeRole(Role.Coordinator),
        name: details.user.email,
        actionDesc: $localize`:Coordinators List|Coordinators list delete item prompt@@coordinators-list-delete-item-prompt:will be removed from the contest in a region`,
        archive: false
      };

      const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
        width: '600px',
        data: data
      });

      dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
        if (this.contest) {
          this.contestService.deleteUser(details.competitionId, this.contest.id, Role.Coordinator, details.user.id).subscribe({
            next: () => {
              if (this.coordinators) {
                const index = this.coordinators.findIndex(c => c.id == details.user.id);
                if (index >= 0) this.coordinators.splice(index, 1);

                usersTable.updateData();

                context.modal.deleteHandled();
              }
            },
            error: error => {
              context.modal.deleteHandled($localize`:Notifications|Contest enrollment delete has failed message@@notifications-contest-enrollment-delete-failed-error:Contest enrollment delete has failed`, error);
            }
          });
        }
      });
    }
  }
}
